// src/components/Filters/TransfersFilterBar.tsx

import React, { useState, useEffect } from 'react';
import { Filter, X, Search, Calendar, Bot, User, ChevronDown, ChevronUp, Hash } from 'lucide-react';
import { FilterParams } from 'types';
import { windowForCallId, toLocalDateTimeInput } from 'utils/callIdWindow';
import { getDefaultDateRange } from 'utils/helpers';

interface TransfersFilterBarProps {
  filters: FilterParams;
  onFilterChange: (filters: FilterParams) => void;
}

const TransfersFilterBar: React.FC<TransfersFilterBarProps> = ({ filters, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState<FilterParams>(filters);
  const [hasChanges, setHasChanges] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setLocalFilters(filters);
    setHasChanges(false);
  }, [filters]);

  const handleChange = (field: keyof FilterParams, value: any) => {
    const next: FilterParams = { ...localFilters, [field]: value };

    // A call id carries its own timestamp, so the date window is DERIVED from it
    // rather than kept or cleared. Both of the obvious answers fail — measured
    // against the live backend, same call id:
    //
    //     no window at all      -> still running at 60s
    //     the UI's 2-day range  -> HTTP 504 after 61s
    //     ±30 min from the id   -> HTTP 200 in 1.2s
    //
    // `callId` is not indexed there, so the window is what makes the query
    // tractable — and pinning it to the call's own moment is both fast AND
    // correct for a call of any age, which the default range never was.
    if (field === 'callId') {
      const w = windowForCallId(value);
      if (w) {
        next.dateFrom = toLocalDateTimeInput(w.fromEpochMs);
        next.dateTo = toLocalDateTimeInput(w.toEpochMs);
      } else if (!value) {
        // Cleared: back to the ordinary window rather than whatever the last
        // call id happened to pin it to.
        Object.assign(next, getDefaultDateRange());
      }
      // A partial id mid-typing yields no window and leaves the dates alone.
    }

    setLocalFilters(next);
    setHasChanges(true);
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    setHasChanges(false);
    setIsExpanded(false);
  };

  const handleClear = () => {
    const defaultFilters = {
      perPage: filters.perPage,
      ...getDefaultDateRange()
    };
    setLocalFilters(defaultFilters);
    onFilterChange(defaultFilters);
    setHasChanges(false);
  };

  const hasActiveFilters = localFilters.transferType || localFilters.status ||
    localFilters.dateFrom || localFilters.dateTo || localFilters.callerId || localFilters.callId;

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.transferType) count++;
    if (localFilters.status) count++;
    if (localFilters.dateFrom || localFilters.dateTo) count++;
    if (localFilters.callerId) count++;
    return count;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Mobile Header - Collapsible */}
      <div className="lg:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-cyan-600" />
            <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">Transfer Filters</span>
            {getActiveFilterCount() > 0 && (
              <span className="px-2 py-0.5 bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-medium rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
            {hasChanges && (
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Mobile Expanded Content */}
        {isExpanded && (
          <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-3 pt-3">
              {/* Transfer Type */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type</label>
                <select
                  value={localFilters.transferType || 'all'}
                  onChange={(e) => handleChange('transferType', e.target.value === 'all' ? undefined : e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="all">All Types</option>
                  <option value="ai">AI Transfer</option>
                  <option value="hotkey">Hotkey Transfer</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Status</label>
                <select
                  value={localFilters.status || 'all'}
                  onChange={(e) => handleChange('status', e.target.value === 'all' ? undefined : e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="all">All Status</option>
                  <option value="answer">Answered</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Date From */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From</label>
                <input
                  type="datetime-local"
                  value={localFilters.dateFrom || ''}
                  onChange={(e) => handleChange('dateFrom', e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Date To */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To</label>
                <input
                  type="datetime-local"
                  value={localFilters.dateTo || ''}
                  onChange={(e) => handleChange('dateTo', e.target.value || undefined)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Caller ID */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Caller ID</label>
                <input
                  type="text"
                  value={localFilters.callerId || ''}
                  onChange={(e) => handleChange('callerId', e.target.value || undefined)}
                  placeholder="Search caller ID..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              {/* Call ID */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Call ID</label>
                <input
                  type="text"
                  value={localFilters.callId || ''}
                  onChange={(e) => handleChange('callId', e.target.value || undefined)}
                  placeholder="Search call ID..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            {/* Mobile Action Buttons */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleApply}
                disabled={!hasChanges}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  hasChanges
                    ? 'bg-cyan-600 text-white active:bg-cyan-700'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                }`}
              >
                <Search className="w-4 h-4" />
                Apply
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClear}
                  className="px-4 py-2.5 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop View - Compact Inline Layout */}
      <div className="hidden lg:block p-3">
        {/* Primary Filters Row */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* Filter Icon & Label */}
          <div className="flex items-center gap-2 pb-2">
            <Filter className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Transfer Filters</span>
          </div>

          {/* Transfer Type */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <Bot className="w-3 h-3" />
              Type
            </label>
            <select
              value={localFilters.transferType || 'all'}
              onChange={(e) => handleChange('transferType', e.target.value === 'all' ? undefined : e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="ai">AI Transfer</option>
              <option value="hotkey">Hotkey Transfer</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select
              value={localFilters.status || 'all'}
              onChange={(e) => handleChange('status', e.target.value === 'all' ? undefined : e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="answer">Answered</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Date From */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              From
            </label>
            <input
              type="datetime-local"
              value={localFilters.dateFrom || ''}
              onChange={(e) => handleChange('dateFrom', e.target.value || undefined)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Date To */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              To
            </label>
            <input
              type="datetime-local"
              value={localFilters.dateTo || ''}
              onChange={(e) => handleChange('dateTo', e.target.value || undefined)}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Caller ID */}
          <div className="flex-shrink-0 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <User className="w-3 h-3" />
              Caller ID
            </label>
            <input
              type="text"
              value={localFilters.callerId || ''}
              onChange={(e) => handleChange('callerId', e.target.value || undefined)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Call ID */}
          <div className="flex-shrink-0 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
              <Hash className="w-3 h-3" />
              Call ID
            </label>
            <input
              type="text"
              value={localFilters.callId || ''}
              onChange={(e) => handleChange('callId', e.target.value || undefined)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Action Buttons - Inline */}
          <div className="flex items-center gap-2 ml-auto pb-0.5">
            <button
              onClick={handleApply}
              disabled={!hasChanges}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                hasChanges
                  ? 'bg-cyan-600 text-white hover:bg-cyan-700 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Search className="w-3.5 h-3.5" />
              Search
            </button>

            {hasActiveFilters && (
              <button
                onClick={handleClear}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md font-medium transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Status indicator */}
        {(hasChanges || (!hasChanges && hasActiveFilters)) && (
          <div className="flex items-center justify-end mt-2">
            {hasChanges && (
              <span className="text-xs text-orange-600 dark:text-orange-400 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                Unsaved changes
              </span>
            )}
            {!hasChanges && hasActiveFilters && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                {getActiveFilterCount()} filter{getActiveFilterCount() > 1 ? 's' : ''} active
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransfersFilterBar;
